import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EditMode, EditModeContext } from '../edit-mode';
import {
  BackHand,
  BackHandFill,
  BlurOn,
  CropSquare,
  Cursor,
  Title,
} from '@crabs-ai/web-icons';
import { EditToolGroup, type ToolConfig } from './tool-dropdown-button';

type ToolGroupConfig = {
  key: string;
  tools: ToolConfig[];
};

const TOOL_GROUPS: ToolGroupConfig[] = [
  {
    key: 'selection',
    tools: [
      {
        mode: 'select',
        titleKey: 'Editor.property-menu.select-mode',
        shortcut: 'V',
        Icon: Cursor,
      },
      {
        mode: 'hand',
        titleKey: 'Editor.property-menu.hand-tool',
        shortcut: 'H',
        Icon: BackHand,
        SelectedIcon: BackHandFill,
      },
    ],
  },
  {
    key: 'shapeBox',
    tools: [
      {
        mode: 'draw-solid',
        titleKey: 'Editor.property-menu.shape',
        shortcut: 'R',
        Icon: CropSquare,
      },
      {
        mode: 'draw-blur',
        titleKey: 'Editor.property-menu.blur',
        shortcut: 'B',
        Icon: BlurOn,
      },
    ],
  },
  {
    key: 'text',
    tools: [
      {
        mode: 'create-text',
        titleKey: 'Editor.property-menu.title',
        shortcut: 'T',
        Icon: Title,
      },
    ],
  },
];

const DEFAULT_GROUP_TOOLS: Record<string, EditMode> = {
  selection: 'select',
  shapeBox: 'draw-solid',
};

export function EditTools() {
  const { editMode, setEditMode } = useContext(EditModeContext);
  const [groupActiveTools, setGroupActiveTools] =
    useState<Record<string, EditMode>>(DEFAULT_GROUP_TOOLS);

  useEffect(() => {
    for (const group of TOOL_GROUPS) {
      if (group.tools.some((tool) => tool.mode === editMode)) {
        setGroupActiveTools((prev) => {
          if (prev[group.key] === editMode) return prev;
          return { ...prev, [group.key]: editMode };
        });
        break;
      }
    }
  }, [editMode]);

  const handleSelectTool = useCallback(
    (groupKey: string, mode: EditMode) => {
      setGroupActiveTools((prev) => ({ ...prev, [groupKey]: mode }));
      setEditMode(mode);
    },
    [setEditMode],
  );

  return (
    <div className="flex items-center">
      {TOOL_GROUPS.map((group) => {
        const activeToolMode =
          groupActiveTools[group.key] ?? group.tools[0]!.mode;
        const activeTool =
          group.tools.find((tool) => tool.mode === activeToolMode) ??
          group.tools[0]!;
        const isGroupActive = group.tools.some(
          (tool) => tool.mode === editMode,
        );

        return (
          <EditToolGroup
            key={group.key}
            tools={group.tools}
            activeTool={activeTool}
            isGroupActive={isGroupActive}
            editMode={editMode}
            onSelectTool={(mode) => handleSelectTool(group.key, mode)}
          />
        );
      })}
    </div>
  );
}
